'use client'

// Shard — the sacrifice offering object. A faceted crystal (shard.glb, vertex-
// coloured: the RED channel of COLOR_0 marks the edges) tinted to the
// submitter's tier colour and sized by tier. No glow (no bloom in the pipe) —
// tier reads from colour + size.
//
// Edge damage: a TSL MeshStandardNodeMaterial uses the red vertex mask × noise
// to break the sharp edges into irregular chips — lighter + rougher where
// chipped, like a worn/fractured crystal. Runs through RendererDither with the
// rest of the world. Tuning constants up top.
//
// ONE SHADER FOR EVERY SHARD. The graph below is built once, at module level,
// and every shard's material points at the same node objects. The tier colour
// is read off the material (materialColor, an ordinary uniform) and the noise
// is seeded from WORLD position rather than a per-shard constant, so nothing
// about one crystal is baked into the program another crystal needs.
//
// World position and not a material property on purpose: three's shadow pass
// swaps in its own override material and folds this colour graph into it for
// the alpha, so anything read off "the material" is read off the override
// there, and a property it does not have is an undefined uniform and a
// crash every frame (found the first time this was tried). Position exists
// in every pass, and no two crystals stand in the same place.
//
// Measured before this on the live site: 33 offerings in the world produced
// 64 distinct shader modules out of 169 total (each shard its own main-pass
// AND shadow-pass program, because three keys a program on node identity and
// every shard used to build its own nodes with its own constants). Each of
// those is a GPU pipeline compile the first time that crystal enters the
// frustum, which is the stutter you feel walking toward the ruins. Now the
// count is two, compiled once.

import { useMemo } from 'react'
import * as THREE from 'three/webgpu'
import {
  attribute, positionWorld, mx_noise_float,
  float, vec3, mix, smoothstep, materialColor,
} from 'three/tsl'
import { useModel } from '../hooks/useModel'
import { ditherMaterial } from '../effects/ditherRegistry'

const MODEL = '/models/shard.glb'

// --- edge-damage tuning ---
const NOISE_SCALE_A = 6.0     // coarse chips
const NOISE_SCALE_B = 15.0    // fine grain
const CHIP_LO = 0.40          // smoothstep band on (red × noise)
const CHIP_HI = 0.68
const CHIP_LIGHTEN = 0.6      // how far chipped edges go toward white (fresh fracture)
const FACE_ROUGH = 0.3        // glossy crystal face
const CHIP_ROUGH = 0.85       // matte chip

// --- per-shard placement variation (so a cluster doesn't look stamped) ---
const MAX_Z_TILT = 0.30       // radians (~17°) — lean off-vertical, pivots at the base

// Deterministic hash of a seed → stable pseudo-random per shard.
function hashSeed(s) {
  const str = String(s)
  let h = 0
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0
  return h
}

// The shared graph. Lazy, because TSL nodes are cheap but not free and the
// look-dev preview imports this file without ever mounting a shard.
let _graph = null
function shardGraph() {
  if (_graph) return _graph

  // Tier colour: material.color, the ordinary uniform every standard
  // material already carries.
  const base = materialColor
  const red = attribute('color', 'vec4').x // edge mask painted in Blender

  // fbm-ish noise from two octaves of MaterialX noise, in world space, so
  // identical clones standing in different places don't share the exact
  // same chip pattern. (A kept crystal moving to the ruins gets a new
  // pattern; nobody has ever watched one closely enough to tell.)
  const p = positionWorld
  const n1 = mx_noise_float(p.mul(NOISE_SCALE_A))
  const n2 = mx_noise_float(p.mul(NOISE_SCALE_B))
  const noise = n1.mul(0.65).add(n2.mul(0.35)).mul(0.5).add(0.5) // ~0..1

  // chips live where the red edge and the noise coincide → irregular, not a clean line
  const chip = smoothstep(float(CHIP_LO), float(CHIP_HI), red.mul(noise))

  const chipColor = mix(base, vec3(1.0, 1.0, 1.0), float(CHIP_LIGHTEN))
  _graph = {
    colorNode: mix(base, chipColor, chip),
    roughnessNode: mix(float(FACE_ROUGH), float(CHIP_ROUGH), chip),
    metalnessNode: float(0.0),
    // The kept things carry their own light. Emissive rather than brighter
    // albedo so the crystals still read at night (albedo multiplies the
    // scene's light; emissive adds after it), the chips glow a shade hotter
    // than the faces, and the MRT bloom pass picks all of it up for free the
    // day that flag flips. Modest on purpose: a display, not a beacon.
    emissiveNode: mix(base.mul(0.4), chipColor.mul(0.6), chip),
  }
  return _graph
}

function makeShardMaterial(hexColor) {
  const g = shardGraph()
  const mat = new THREE.MeshStandardNodeMaterial()
  mat.vertexColors = false // don't auto-multiply; we read the red channel as a mask
  mat.color = new THREE.Color(hexColor)
  mat.colorNode = g.colorNode
  mat.roughnessNode = g.roughnessNode
  mat.metalnessNode = g.metalnessNode
  mat.emissiveNode = g.emissiveNode
  // Dithered from birth. Left to the sweep, a new crystal rendered clean
  // for up to three seconds and then took a second compile to get the
  // grain, both of them in front of whoever was looking at it.
  ditherMaterial(mat)
  return mat
}

export function Shard({ color = '#a78bfa', position = [0, 0, 0], scale = 1, seed }) {
  const { scene } = useModel(MODEL)
  const h = hashSeed(seed ?? `${color}|${position.join(',')}`)

  // Clone per instance (shared geometry, own material) so many shards of
  // different tier colours can coexist on the one program.
  const object = useMemo(() => {
    const s = scene.clone(true)
    const mat = makeShardMaterial(color)
    s.traverse((o) => {
      if (!o.isMesh) return
      o.material = mat
      o.castShadow = true
      o.receiveShadow = true
      // Born after load: let the Precompiler build its pipeline before
      // anyone turns to look at it.
      o.userData.precompile = true
    })
    return s
  }, [scene, color])

  // Per-shard rotation from the same stable seed: full random Y-facing + a
  // Z-lean so no two crystals sit the same. The shard's origin is at its base,
  // so the Z-tilt pivots there (leans, stays planted).
  const rotation = useMemo(() => {
    const yaw = ((h % 1000) / 1000) * Math.PI * 2
    const tilt = (((h >>> 10) % 1000) / 1000 * 2 - 1) * MAX_Z_TILT
    return [0, yaw, tilt]
  }, [h])

  return <primitive object={object} position={position} scale={scale} rotation={rotation} />
}

export default Shard
